import React, { useState, useEffect, use } from "react";
import sample_image from "assets/image.png";
import { PieChart, Pie, Cell } from "recharts";
import TransactionModal from "./AddNewTransaction";
import MaterialPurchaseForm from "./MaterialPurchaseForm";
import WorkersPaymentForm from "./FinanceWorkerPayment";
import OtherPaymentForm from "./OtherPaymentForm";
import RecievedPaymentForm from "./RecievedPaymentForm";
import RefundForm from "./RefundPaymentForm";
import { useAlert } from "components/UI_components/Alert";
import {
  ClipboardList,
  Package,
  Users,
  RefreshCw,
  Clock,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Download,
  Loader,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";
import MaterialPurchaseDetails from "./MaterialPurchaseDetails";
import ReceiptDownloadButton from "./ReceiptDownloadButton";
import { MdOutlinePayments } from "react-icons/md";
import { AiOutlineVerticalAlignBottom } from "react-icons/ai";
import { FinanceApi } from "api/finance/service";
import FinanceIcon from "assets/empty state/finance.svg";
import { convertToStringIndex } from "screens/Dashboard/Contractors/ContractorDetails";
// import ReceivedForm from "./RecievedForm";
import LoadingScreen from "components/UI_components/LoadingScreen";
import PettyCashDashboard from "./PettyCashDashboard";
import ImageZoomModal from "../../Dashboard/Design/components/ImageZoomModal";

const COLORS = ["#46237A", "#6B3FA0", "#8F6AC2", "#B394E8", "#D7C6F5"];

const PartPaymentDialog = ({
  isOpen,
  onClose,
  onSubmit,
  deliveryId,
  remainingAmount = 0,
}) => {
  const [formData, setFormData] = useState({
    paid_amount: "",
    remarks: "",
    payment_method: "UPI/Bank Transfer",
    file: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const remaining = Math.max(0, Number(remainingAmount) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Create Part Payment</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paid Amount
            </label>
            <input
              type="number"
              required
              className="w-full border rounded-lg p-2"
              min={0}
              max={remaining}
              value={formData.paid_amount}
              onChange={(e) => {
                const raw = e.target.value;

                if (raw === "") {
                  setFormData({ ...formData, paid_amount: "" });
                  return;
                }

                const next = Number(raw);
                if (!Number.isFinite(next)) return;

                const clamped = Math.min(Math.max(next, 0), remaining);
                setFormData({
                  ...formData,
                  paid_amount: String(clamped),
                });
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              className="w-full border rounded-lg p-2"
              value={formData.payment_method}
              onChange={(e) =>
                setFormData({ ...formData, payment_method: e.target.value })
              }
            >
              <option value="UPI/Bank Transfer">UPI/Bank Transfer</option>
              <option value="Cash">Cash</option>
              <option value="Online">Online</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              className="w-full border rounded-lg p-2"
              rows="3"
              value={formData.remarks}
              onChange={(e) =>
                setFormData({ ...formData, remarks: e.target.value })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attachment (PDF/Image)
            </label>
            <input
              type="file"
              accept=".pdf,image/*"
              className="w-full border rounded-lg p-2"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  file: e.target.files?.[0] || null,
                })
              }
            />
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : "Update Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MaterialPurchaseModal = ({
  transaction,
  onClose,
  setShowMaterialPurchaseDetails,
  setDelivery,
  isPendingView = false,
  canEditFinance = false,
}) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [deliveryData, setDeliveryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isPaymentHistoryLoading, setIsPaymentHistoryLoading] = useState(false);
  const [showPartPaymentModal, setShowPartPaymentModal] = useState(false);

  useEffect(() => {
    const fetchDeliveryData = async () => {
      if (transaction?.id) {
        setIsLoading(true);
        setError(null);
        try {
          const response = await FinanceApi.getDeliveryById(transaction.id);
          if (response.status && response.data) {
            setDeliveryData(response.data);
          } else {
            setError("Failed to fetch delivery details");
          }
        } catch (err) {
          console.error("Error fetching delivery data:", err);
          setError("Error loading delivery details");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDeliveryData();
  }, [transaction?.id]);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (transaction?.id) {
        setIsPaymentHistoryLoading(true);
        try {
          const response = await FinanceApi.listPartPaymentsOfDelivery({
            delivery: transaction.id,
          });
          if (response.status && response.data) {
            setPaymentHistory(response.data);
          }
        } catch (err) {
          console.error("Error fetching payment history:", err);
        } finally {
          setIsPaymentHistoryLoading(false);
        }
      }
    };

    fetchPaymentHistory();
  }, [transaction?.id]);

  const handlePartPaymentSubmit = async (paymentData) => {
    try {
      const formData = new FormData();
      formData.append("delivery", transaction.id);
      formData.append("paid_amount", paymentData.paid_amount);
      formData.append("remarks", paymentData.remarks);
      formData.append("payment_method", paymentData.payment_method);
      if (paymentData.file) {
        formData.append("file", paymentData.file);
      }

      const response = await FinanceApi.createPartPayment(formData);
      if (response.status) {
        setShowPartPaymentModal(false);
        // Refresh history
        const updatedHistory = await FinanceApi.listPartPaymentsOfDelivery({
          delivery: transaction.id,
        });
        if (updatedHistory.status) setPaymentHistory(updatedHistory.data);

        // Also refresh delivery data to update remaining amount
        const updatedDelivery = await FinanceApi.getDeliveryById(
          transaction.id,
        );
        if (updatedDelivery.status) setDeliveryData(updatedDelivery.data);
      }
    } catch (err) {
      console.error("Error creating part payment:", err);
    }
  };

  if (!transaction) return null;

  // Use fetched data if available, otherwise fall back to transaction prop
  const displayData = deliveryData || transaction;

  const handleImageClick = () => {
    setIsImageZoomed(true);
  };

  const handleZoomClose = () => {
    setIsImageZoomed(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Material Purchase Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4 h-[80%] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader className="animate-spin text-purple-600" size={40} />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-red-600 mb-2">{error}</p>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Vendor Details */}
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Vendor/Shop Name</span>
                  <div className="text-right">
                    <div>{displayData.vendor_name}</div>
                    <div className="text-sm text-gray-500">
                      {displayData.contact_no}
                    </div>
                  </div>
                </div>

                {/* Transaction Details */}
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Date</span>
                  <div className="text-right">
                    <div>
                      {new Date(displayData.date).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatTransactionDateTime(displayData)}
                    </div>
                  </div>
                </div>

                {/* Materials List */}
                <div className="space-y-2">
                  <span className="text-gray-600 block">
                    Materials ({displayData.all_materials?.length || 0})
                  </span>
                  <div className="space-y-3">
                    {displayData.all_materials?.map((material) => {
                      const basePrice =
                        (material.unit_price || 0) * (material.quantity || 0);
                      const taxAmount = material.tax_amount || 0;
                      const totalPrice = basePrice + taxAmount;
                      const taxPercentage =
                        basePrice > 0 ? (taxAmount / basePrice) * 100 : 0;

                      return (
                        <div
                          key={material.id}
                          className="bg-gray-50 p-4 rounded-lg"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">
                                {material.material_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {material.material_subcategory}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                ₹{totalPrice.toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {material.quantity} {material.unit} × ₹
                                {material.unit_price} = ₹
                                {basePrice.toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-400">
                                Tax: ₹{taxAmount.toLocaleString()} (
                                {taxPercentage.toFixed(2)}%)
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Payment Details */}
                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Payment Method</span>
                  <span>{displayData.payment_method}</span>
                </div>

                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Payment Status</span>
                  <span className="capitalize">
                    {displayData.payment_status}
                  </span>
                </div>

                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="text-xl font-semibold">
                    ₹{displayData.total_price?.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Paid Amount</span>
                  <span className="text-green-600">
                    ₹{displayData.paid_amount?.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between items-center py-3 border-b">
                  <span className="text-gray-600">Remaining Amount</span>
                  <span className="text-red-600">
                    ₹{displayData.remaining_payment?.toLocaleString()}
                  </span>
                </div>

                {displayData.remarks && (
                  <div className="space-y-2">
                    <span className="text-gray-600 block">Remarks</span>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                      <div>{displayData.remarks}</div>
                    </div>
                  </div>
                )}

                {/* Payment History */}
                <div className="space-y-2 py-3">
                  <span className="text-gray-600 block">Payment History</span>
                  {isPaymentHistoryLoading ? (
                    <div className="flex justify-center py-2">
                      <Loader
                        className="animate-spin text-purple-600"
                        size={20}
                      />
                    </div>
                  ) : paymentHistory.length > 0 ? (
                    <div className="space-y-2">
                      {paymentHistory.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex justify-between items-center text-sm bg-gray-50 p-3 rounded-lg"
                        >
                          <div>
                            <div className="font-medium text-gray-700">
                              ₹{payment.amount?.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              {payment.payment_mathod} •{" "}
                              {new Date(payment.created_on).toLocaleDateString(
                                "en-IN",
                              )}
                            </div>
                            {payment.remarks && (
                              <div className="text-xs text-gray-400 mt-1 italic">
                                {payment.remarks}
                              </div>
                            )}
                          </div>
                          {payment.file && (
                            <a
                              href={payment.file}
                              target="_blank"
                              rel="noreferrer"
                              className="text-purple-600 hover:text-purple-800 inline-flex items-center gap-1"
                            >
                              <Download size={14} />
                              <span className="text-xs font-medium">
                                View File
                              </span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">
                      No payment history found.
                    </div>
                  )}
                </div>

                {/* Receipt Images - Updated with clickable zoom */}
                {displayData.receipt_image && (
                  <div className="space-y-2">
                    <span className="text-gray-600 block">Receipt</span>
                    <div
                      className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={handleImageClick}
                      title="Click to zoom"
                    >
                      <img
                        src={displayData.receipt_image}
                        alt="Receipt"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div
                  className={`flex ${canEditFinance &&
                    Number(displayData?.remaining_payment ?? 0) > 0
                      ? "justify-between"
                      : "justify-end"
                    } p-6 border-t`}
                >
                  {canEditFinance &&
                    Number(displayData?.remaining_payment ?? 0) > 0 && (
                    <button
                      onClick={() => {
                        setShowPartPaymentModal(true);
                      }}
                      className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 flex items-center"
                    >
                      Update Payment
                    </button>
                    )}

                  {!isPendingView && (
                    <ReceiptDownloadButton
                      transaction={displayData}
                      paymentHistory={paymentHistory}
                      type="material"
                      buttonText={
                        <div className="flex items-center gap-2">
                          <Download size={16} />
                          <span>View Receipt</span>
                        </div>
                      }
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {/* {isImageZoomed && displayData?.receipt_image && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]">
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              onClick={handleZoomClose}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              title="Close zoom"
            >
              <X size={32} />
            </button>
            <img
              src={displayData.receipt_image}
              alt="Receipt - Zoomed"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )} */}
      <ImageZoomModal
        isOpen={isImageZoomed}
        onClose={handleZoomClose}
        imageUrl={displayData?.receipt_image}
        itemName="Receipt Image"
      />

      <PartPaymentDialog
        isOpen={showPartPaymentModal}
        onClose={() => setShowPartPaymentModal(false)}
        onSubmit={handlePartPaymentSubmit}
        deliveryId={transaction?.id}
        remainingAmount={displayData?.remaining_payment}
      />
    </>
  );
};

const WorkerTransactionModal = ({ transaction, onClose }) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  if (!transaction) return null;
  console.log("Worker transaction data:", transaction);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Workers Payment Details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div
            className="p-6 space-y-4 h-[80%]"
            style={{
              overflowY: "scroll",
            }}
          >
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Receiver</span>
              <div className="text-right">
                <div>{transaction.worker_name}</div>
                <div className="text-sm text-gray-500">
                  {transaction.worker_workname}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Receiver Phone Number</span>
              <span>{transaction?.Worker_phone}</span>
            </div>
            {/* Created By */}
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Created By</span>
              <div className="text-right">
                <div>{transaction.created_by}</div>
                {transaction.last_updated_by && (
                  <div className="text-sm text-gray-500">
                    Last updated by: {transaction.last_updated_by}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Date</span>
              <div className="text-right">
                <div>
                  {new Date(
                    transaction.date || transaction.transaction_date,
                  ).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long", // Keep "long" for full month name like "July" instead of "Jul"
                    year: "numeric",
                  })}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTransactionDateTime(transaction)}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Amount</span>
              <span className="text-xl font-semibold">
                ₹{transaction.amount?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Method</span>
              <span>{transaction.transaction_method}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Payment Type</span>
              <span>{transaction.payment_type}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Total Paid Amount</span>
              <span className="text-green-600">
                ₹{(transaction.amount || 0).toLocaleString()}
              </span>
            </div>
            {/* Comments Section */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Comments/Remarks</span>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                <div>{transaction?.comments}</div>
              </div>
            </div>
            {/* Payment Slips */}
            {transaction.receipt_image && (
              <div className="space-y-2">
                <span className="text-gray-600 block">Payment Slips</span>
                <div className="flex gap-4">
                  <div
                    className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setIsImageZoomed(true)}
                  >
                    <img
                      src={transaction.receipt_image}
                      alt="Payment slip - click to zoom"
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}
            {/* Footer */}
            <div className="flex justify-end p-6 border-t">
              <ReceiptDownloadButton
                transaction={transaction}
                type="worker"
                buttonText={
                  <div className="flex items-center gap-2">
                    <Download size={16} />
                    <span>View Receipt</span>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {/* {isImageZoomed && transaction.receipt_image && (
        <div className="fixed inset-0 bg-opacity-30 flex items-center justify-center z-[60]">
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setIsImageZoomed(false)}
              className="absolute top-2 right-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors z-10"
            >
              <X size={24} />
            </button>
            <img
              src={transaction.receipt_image}
              alt="Payment slip - zoomed view"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )} */}
      <ImageZoomModal
        isOpen={isImageZoomed}
        onClose={() => setIsImageZoomed(false)}
        imageUrl={transaction?.receipt_image}
        itemName="Payment Slip"
      />
    </>
  );
};

const RefundTransactionModal = ({ transaction, onClose }) => {
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  if (!transaction) return null;

  const refundPurpose =
    transaction.refund_purpose ||
    transaction.refundPurpose ||
    transaction.refund_reason ||
    "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl ">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Refund Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          className="p-6 space-y-4 h-[80%]"
          style={{
            overflowY: "scroll",
          }}
        >
          {/* Transaction Type */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Transaction Type</span>
            <span>{transaction.transaction_type}</span>
          </div>

          {/* Purpose of Refund */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Purpose of Refund</span>
            <span className="text-right">{refundPurpose || "N/A"}</span>
          </div>

          {/* Date */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Date</span>
            <div className="text-right">
              <div>
                {new Date(
                  transaction.date || transaction.transaction_date,
                ).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long", // Keep "long" for full month name like "July" instead of "Jul"
                  year: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {formatTransactionDateTime(transaction)}
              </div>
            </div>
          </div>

          {/* Refund Amount */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Refund Amount</span>
            <span className="text-xl font-semibold">
              ₹{transaction.amount?.toLocaleString()}
            </span>
          </div>

          {/* Refund Method */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Refund Method</span>
            <span>{transaction.transaction_method}</span>
          </div>

          {/* Refund Status */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Refund Status</span>
            <span>{transaction.transaction_status}</span>
          </div>

          {/* Comments/Remarks Section */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Comments/Remarks</span>
              {/* <button className="p-2 hover:bg-purple-50 rounded-full text-purple-700">
                <Plus size={20} />
              </button> */}
            </div>

            <div className="space-y-3">
              {/* Latest Comment */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="text-sm text-gray-500">
                  {new Date(
                    transaction.date || transaction.transaction_date,
                  ).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <div>{transaction.comments || "—"}</div>
              </div>
            </div>
          </div>

          {transaction.receipt_url && (
            <div className="space-y-2">
              <span className="text-gray-600 block">Payment Slips</span>
              <div className="flex gap-4">
                <div
                  className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setIsImageZoomed(true)}
                  title="Click to zoom"
                >
                  <img
                    src={transaction.receipt_url}
                    alt="Payment slip"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          <ImageZoomModal
            isOpen={isImageZoomed}
            onClose={() => setIsImageZoomed(false)}
            imageUrl={transaction?.receipt_url}
            itemName="Payment Slip"
          />
          {/* Footer */}
          <div className="flex justify-between p-6 border-t">
            {/* <button className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 flex items-center">
            Edit Details
          </button>
          <button className="px-6 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 flex items-center">
            Delete Transaction Record
          </button> */}
            <ReceiptDownloadButton
              transaction={transaction}
              type="refund"
              buttonText={
                <div className="flex items-center gap-2">
                  <Download size={16} />
                  <span>View Receipt</span>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const OtherTransactionModal = ({
  transaction,
  onClose,
  onUpdate,
  isReceivedTransaction = false,
  canEditFinance = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editedTransaction, setEditedTransaction] = useState({
    transaction_type: transaction?.transaction_type || "",
    transaction_method: transaction?.transaction_method || "",
    comments: transaction?.comments || "",
    amount: transaction?.amount || 0,
    transaction_date: transaction?.transaction_date
      ? new Date(transaction.transaction_date).toISOString().split("T")[0]
      : "",
  });

  if (!transaction) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedTransaction((prev) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
  };

  const handleSaveChanges = async () => {
    if (!editedTransaction.transaction_type?.trim()) {
      alert("Transaction Type cannot be blank");
      return;
    }
    try {
      const accessToken = localStorage.getItem("access");
      const requestBody = {
        ...editedTransaction,
        sites: localStorage.getItem("site"),
      };
      const response = await fetch(
        `${BASE_URL}/finance/update-general-transaction/${transaction.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (response.ok) {
        // If update was successful
        setIsEditing(false);
        if (onUpdate) onUpdate(); // Refresh data if needed
        onClose();
      } else {
        console.error("Failed to update transaction");
        // Handle error (show error message to user)
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
    }
  };

  const handleDelete = async () => {
    try {
      // Get base URL from env or your config
      const accessToken = localStorage.getItem("access");

      const response = await fetch(
        `${BASE_URL}/finance/delete-general-transaction/${transaction.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sites: localStorage.getItem("site"),
          }),
        },
      );

      if (response.ok) {
        // If deletion was successful
        setShowDeleteConfirmation(false);
        onClose();
        if (onUpdate) onUpdate(); // Refresh data if needed
      } else {
        console.error("Failed to delete transaction");
        // Handle error (show error message to user)
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {isEditing
              ? "Edit Transaction"
              : isReceivedTransaction
                ? "Received Transaction Details"
                : "Other Transaction Details"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          className="p-6 space-y-4 overflow-y-auto"
          style={{
            height: "calc(70vh - 130px)", // Adjusted to account for header and footer
          }}
        >
          {isEditing ? (
            // Edit Form
            <>
              {/* Transaction Type */}
              <div className="space-y-2">
                <label className="block text-gray-600">Transaction Type</label>
                <input
                  type="text"
                  name="transaction_type"
                  value={editedTransaction.transaction_type}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Transaction Method */}
              <div className="space-y-2">
                <label className="block text-gray-600">Payment Method</label>
                <select
                  name="transaction_method"
                  value={editedTransaction.transaction_method}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a method</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI/Bank Transfer">UPI/Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <label className="block text-gray-600">Amount</label>
                <input
                  type="number"
                  name="amount"
                  value={editedTransaction.amount}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Transaction Date */}
              <div className="space-y-2">
                <label className="block text-gray-600">Transaction Date</label>
                <input
                  type="date"
                  name="transaction_date"
                  value={editedTransaction.transaction_date}
                  onChange={handleEditChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <label className="block text-gray-600">Remarks</label>
                <textarea
                  name="comments"
                  value={editedTransaction.comments}
                  onChange={handleEditChange}
                  rows={4}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </>
          ) : (
            // View Details
            <>
              {/* Transaction Type */}
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Transaction Type</span>
                <div className="text-right">
                  <div>{transaction.transaction_type}</div>
                </div>
              </div>

              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Transaction Category</span>
                <div className="text-right">
                  <div>
                    {isReceivedTransaction ? "Received Payment" : "Other"}
                  </div>
                </div>
              </div>

              {/* Created By */}
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Created By</span>
                <div className="text-right">
                  <div>{transaction.created_by}</div>
                  <div className="text-sm text-gray-500">
                    Last updated by: {transaction.last_updated_by}
                  </div>
                </div>
              </div>

              {/* Transaction Date */}
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Transaction Date</span>
                <div className="text-right">
                  <div>
                    {new Date(transaction.transaction_date).toLocaleDateString(
                      "en-IN",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      },
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatTransactionDateTime(transaction)}
                  </div>
                </div>
              </div>

              {/* Amount Details */}
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Amount</span>
                <span className="text-xl font-semibold">
                  ₹{transaction.amount?.toLocaleString()}
                </span>
              </div>

              {/* Payment Method */}
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Payment Method</span>
                <span>{transaction.transaction_method}</span>
              </div>

              {/* Creation Details */}
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Created On</span>
                <div className="text-right">
                  <div>
                    {new Date(transaction.created_on).toLocaleString("en-IN")}
                  </div>
                  <div className="text-sm text-gray-500">
                    Last updated:{" "}
                    {new Date(transaction.last_updated_on).toLocaleString(
                      "en-IN",
                    )}
                  </div>
                </div>
              </div>

              {/* Remarks Section */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Remarks</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg space-y-1">
                  <div>{transaction.comments}</div>
                </div>
              </div>

              {/* Receipt Image Section */}
              {transaction.ireceipt_image && (
                <div className="space-y-2">
                  <span className="text-gray-600 block">Receipt Image</span>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div
                      className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setShowImageModal(true)}
                    >
                      <img
                        src={transaction.ireceipt_image}
                        alt="Receipt"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Click to view full size
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Footer */}
          <div
            className={`flex ${canEditFinance ? "justify-between" : "justify-end"} p-6 border-t`}
          >
            {isEditing && canEditFinance ? (
              <>
                <button
                  onClick={handleSaveChanges}
                  disabled={!editedTransaction.transaction_type?.trim()}
                  className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                {canEditFinance && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-6 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-purple-800 flex items-center"
                  >
                    Edit Details
                  </button>
                )}
                {canEditFinance && (
                  <button
                    onClick={() => setShowDeleteConfirmation(true)}
                    className="px-6 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 flex items-center"
                  >
                    Delete Transaction Record
                  </button>
                )}
                <ReceiptDownloadButton
                  transaction={transaction}
                  type={isReceivedTransaction ? "received" : "other"}
                  buttonText={
                    <div className="flex items-center gap-2">
                      <Download size={16} />
                      <span>View Receipt</span>
                    </div>
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Confirm Deletion</h3>
            <p className="mb-6">
              Are you sure you want to delete this transaction? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {/* {showImageModal && transaction.ireceipt_image && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
            <img
              src={transaction.ireceipt_image}
              alt="Receipt - Full Size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )} */}
      {/* Image Zoom Modal */}
      <ImageZoomModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        imageUrl={transaction.ireceipt_image}
        itemName="Receipt Image"
      />
    </div>
  );
};

const WorkerPendingModal = ({ transaction, onClose }) => {
  if (!transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg h-[70vh] w-full max-w-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            Worker Pending Payment Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          className="p-6 space-y-4"
          style={{
            height: "calc(70vh - 160px)",
            overflowY: "scroll",
          }}
        >
          {/* Worker Info */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Worker</span>
            <div className="text-right">
              <div className="font-medium">{transaction.worker_name}</div>
              <div className="text-sm text-gray-500">
                {transaction.worker_workname}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Description</span>
            <span>{transaction.description}</span>
          </div>

          {/* Date */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Date</span>
            <div className="text-right">
              <div>
                {new Date(transaction.date).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </div>
          </div>

          {/* Attendance Information */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h3 className="font-medium text-gray-700">Attendance Details</h3>

            <div className="flex justify-between items-center">
              <span className="text-gray-600">Days Present</span>
              <span className="font-medium">
                {transaction.days_present || 0} days
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-gray-600">Half Days</span>
              <span className="font-medium">
                {transaction.half_days || 0} days
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-gray-600">Overtime Days</span>
              <span className="font-medium">
                {transaction.overtime_days || 0} days
              </span>
            </div>
          </div>

          {/* Payment Details */}
          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Total Amount</span>
            <span className="text-xl font-semibold">
              ₹{transaction.total_amount?.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Paid Amount</span>
            <span className="text-green-600 font-medium">
              ₹{transaction.total_paid?.toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between items-center py-3 border-b">
            <span className="text-gray-600">Pending Amount</span>
            <span className="text-red-600 font-semibold text-xl">
              ₹{transaction.pending_amount?.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const RefundStats = ({ transactions }) => {
  // Group refunds by payment method and calculate totals
  const refundStats = transactions.reduce((acc, transaction) => {
    const method = transaction.transaction_method || "Unknown";
    if (!acc[method]) {
      acc[method] = {
        name: method,
        value: 0,
        amount: 0,
        count: 0,
      };
    }
    acc[method].amount += Math.abs(transaction.amount);
    acc[method].count += 1;
    return acc;
  }, {});

  const chartData = Object.values(refundStats)
    .sort((a, b) => b.amount - a.amount)
    .map((stat) => ({
      ...stat,
      value: Math.round(
        (stat.amount /
          transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0)) *
        100,
      ),
    }));

  const COLORS = ["#46237A", "#6B3FA0", "#8F6AC2", "#B394E8", "#D7C6F5"];
  const totalRefundAmount = transactions.reduce(
    (sum, t) => sum + Math.abs(t.amount),
    0,
  );

  return (
    <div className="flex items-center justify-between bg-white p-6 rounded-lg mb-6">
      <div className="w-64 h-64 relative">
        <PieChart width={280} height={280}>
          <Pie
            data={chartData}
            cx={125}
            cy={125}
            startAngle={90}
            endAngle={-270}
            innerRadius={85}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-gray-500">Total Refunds</div>
          <div className="text-xl font-semibold text-red-500">
            ₹{totalRefundAmount.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex-1 ml-8">
        <h3 className="text-lg font-medium mb-4">Refund Distribution</h3>
        <div className="space-y-3">
          {chartData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm">{item.name}</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {item.count} refunds
                </span>
                <span className="font-medium w-16 text-right">
                  {item.value}%
                </span>
                <span className="text-red-500 w-24 text-right">
                  ₹{item.amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const OtherStats = ({ transactions }) => {
  // Group refunds by payment method and calculate totals
  const refundStats = transactions.reduce((acc, transaction) => {
    const method = transaction.transaction_method || "Unknown";
    if (!acc[method]) {
      acc[method] = {
        name: method,
        value: 0,
        amount: 0,
        count: 0,
      };
    }
    acc[method].amount += Math.abs(transaction.amount);
    acc[method].count += 1;
    return acc;
  }, {});

  const chartData = Object.values(refundStats)
    .sort((a, b) => b.amount - a.amount)
    .map((stat) => ({
      ...stat,
      value: Math.round(
        (stat.amount /
          transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0)) *
        100,
      ),
    }));

  const COLORS = ["#46237A", "#6B3FA0", "#8F6AC2", "#B394E8", "#D7C6F5"];
  const totalRefundAmount = transactions.reduce(
    (sum, t) => sum + Math.abs(t.amount),
    0,
  );

  return (
    <div className="flex items-center justify-between bg-white p-6 rounded-lg mb-6">
      <div className="w-64 h-64 relative">
        <PieChart width={280} height={280}>
          <Pie
            data={chartData}
            cx={125}
            cy={125}
            startAngle={90}
            endAngle={-270}
            innerRadius={85}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-gray-500">Other Payments</div>
          <div className="text-xl font-semibold text-red-500">
            ₹{totalRefundAmount.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex-1 ml-8">
        <h3 className="text-lg font-medium mb-4">Other Payment Distribution</h3>
        <div className="space-y-3">
          {chartData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm">{item.name}</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {item.count} refunds
                </span>
                <span className="font-medium w-16 text-right">
                  {item.value}%
                </span>
                <span className="text-red-500 w-24 text-right">
                  ₹{item.amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ReceivedStats = ({ transactions }) => {
  // Group refunds by payment method and calculate totals
  const refundStats = transactions.reduce((acc, transaction) => {
    const method = transaction.transaction_method || "Unknown";
    if (!acc[method]) {
      acc[method] = {
        name: method,
        value: 0,
        amount: 0,
        count: 0,
      };
    }
    acc[method].amount += Math.abs(transaction.amount);
    acc[method].count += 1;
    return acc;
  }, {});

  const chartData = Object.values(refundStats)
    .sort((a, b) => b.amount - a.amount)
    .map((stat) => ({
      ...stat,
      value: Math.round(
        (stat.amount /
          transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0)) *
        100,
      ),
    }));

  const COLORS = ["#46237A", "#6B3FA0", "#8F6AC2", "#B394E8", "#D7C6F5"];
  const totalRefundAmount = transactions.reduce(
    (sum, t) => sum + Math.abs(t.amount),
    0,
  );

  return (
    <div className="flex items-center justify-between bg-white p-6 rounded-lg mb-6">
      <div className="w-64 h-64 relative">
        <PieChart width={280} height={280}>
          <Pie
            data={chartData}
            cx={125}
            cy={125}
            startAngle={90}
            endAngle={-270}
            innerRadius={85}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-gray-500">Received Payments</div>
          <div className="text-xl font-semibold text-red-500">
            ₹{totalRefundAmount.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex-1 ml-8">
        <h3 className="text-lg font-medium mb-4">
          Received Payment Distribution
        </h3>
        <div className="space-y-3">
          {chartData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm">{item.name}</span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {item.count} refunds
                </span>
                <span className="font-medium w-16 text-right">
                  {item.value}%
                </span>
                <span className="text-red-500 w-24 text-right">
                  ₹{item.amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RefundRow = ({ transaction, handleClick }) => {
  return (
    <div
      className="flex items-center p-4 hover:bg-gray-50 border-b"
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
        <RefreshCw className="text-purple-700" />
      </div>
      <div className="flex-grow">
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="font-medium text-red-500">
              -₹{Math.abs(transaction?.amount).toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm ml-2">
              {transaction?.transaction_method}
            </span>
          </div>
          <div className="text-gray-500 text-sm">
            {formatTransactionDateTime(transaction)}
          </div>
        </div>
        <div className="text-sm text-gray-600">
          Refund Type: {transaction?.transaction_type}
          <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
            {transaction?.transaction_status}
          </span>
        </div>
        <div className="text-sm text-gray-500 mt-1">
          Original Transaction #{transaction?.original_transaction_id} • Created
          by {transaction?.created_by}
        </div>
      </div>
    </div>
  );
};

const WorkerRow = ({ transaction, handleClick }) => {
  return (
    <div
      className="flex items-center p-4 hover:bg-gray-50 border-b"
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
        <Users className="text-purple-700" />
      </div>
      <div className="flex-grow">
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="font-medium">
              ₹{transaction?.amount?.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm ml-2">
              {transaction?.transaction_method}
            </span>
          </div>
          <div className="text-gray-500 text-sm">
            {formatTransactionDateTime(transaction)}
          </div>
        </div>
        <div className="text-sm text-gray-600">
          Worker: {transaction?.worker_name} ({transaction?.worker_workname})
        </div>
        <div className="text-sm text-gray-500 mt-1">
          {transaction?.payment_type} Payment #{transaction?.id}
        </div>
      </div>
    </div>
  );
};

// Worker stats component
const WorkerStats = ({ transactions }) => {
  // Group workers by category and calculate total payments
  const workerStats = transactions.reduce((acc, transaction) => {
    const category = transaction.worker_workname;
    if (!acc[category]) {
      acc[category] = {
        name: category,
        value: 0,
        amount: 0,
      };
    }
    acc[category].amount += transaction.amount;
    return acc;
  }, {});
  // console.log(workerStats);
  const chartData = Object.values(workerStats)
    .sort((a, b) => b.amount - a.amount)
    .map((stat) => ({
      ...stat,
      value: Math.round(
        (stat.amount / transactions.reduce((sum, t) => sum + t.amount, 0)) *
        100,
      ),
    }));

  const COLORS = ["#46237A", "#6B3FA0", "#8F6AC2", "#B394E8", "#D7C6F5"];

  const totalPaid = transactions.reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="flex items-center justify-between bg-white p-6 rounded-lg mb-6">
      <div className="w-64 h-64 relative">
        <PieChart width={280} height={280}>
          <Pie
            data={chartData}
            cx={125}
            cy={125}
            startAngle={90}
            endAngle={-270}
            innerRadius={85}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-gray-500">Total Paid</div>
          <div className="text-xl font-semibold">
            ₹{totalPaid.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="flex-1 ml-8">
        <h3 className="text-lg font-medium mb-4">
          Worker Payment Distribution
        </h3>
        <div className="space-y-3">
          {chartData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm">{item.name}</span>
              </div>

              <div className="flex flex-row items-center gap-4 w-[15%] justify-between">
                <span className="font-medium">{item.value}%</span>
                <span className="text-gray-500">
                  ₹{item.amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MaterialRow = ({ transaction, handleClick }) => {
  const totalMaterials = transaction?.all_materials?.reduce(
    (acc, curr) => acc + curr.quantity,
    0,
  );
  const mainMaterial =
    transaction?.all_materials?.length > 0 ? transaction?.all_materials[0] : {};

  return (
    <div
      className="flex items-center p-4 hover:bg-gray-50 border-b cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
        <Package className="text-purple-700" />
      </div>
      <div className="flex-grow">
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="font-medium">
              ₹{transaction?.paid_amount?.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm ml-2">
              {transaction?.payment_method}
            </span>
          </div>
          <div className="text-gray-500 text-sm">
            {formatTransactionDateTime(transaction)}
          </div>
        </div>
        <div className="text-sm text-gray-600">
          Material: {mainMaterial?.material_name} (
          {mainMaterial?.material_subcategory}) - {totalMaterials}{" "}
          {mainMaterial?.unit}
        </div>
        <div className="text-sm text-gray-500 mt-1">
          Delivery #{transaction?.id}-{transaction?.transaction_id}
        </div>
      </div>
    </div>
  );
};
const PendingRow = ({ transaction, handleClick }) => {
  const totalMaterials = transaction?.all_materials?.reduce(
    (acc, curr) => acc + curr.quantity,
    0,
  );
  const mainMaterial =
    transaction?.all_materials?.length > 0 ? transaction?.all_materials[0] : {};
  return (
    <div
      className="flex items-center p-4 hover:bg-gray-50 border-b cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
        <Package className="text-purple-700" />
      </div>
      <div className="flex-grow">
        <div className="flex justify-between items-start mb-2">
          <div>
            <span className="font-semibold text-lg text-red-700">
              ₹{transaction?.remaining_payment?.toLocaleString()}
            </span>
            <span className="text-gray-500 text-sm ml-2">
              {transaction?.payment_method}
            </span>
          </div>
          <div className="text-gray-500 text-sm">
            {formatTransactionDateTime(transaction)}
          </div>
        </div>
        <div className="text-sm text-gray-600">
          Material: {mainMaterial?.material_name} (
          {mainMaterial?.material_subcategory}) - {totalMaterials}{" "}
          {mainMaterial?.unit}
        </div>

        <div className="text-sm text-gray-500 mt-1">
          Delivery #{transaction?.id}-{transaction?.transaction_id}
        </div>
      </div>
    </div>
  );
};

const MaterialStats = ({ transactions }) => {
  const [showAll, setShowAll] = useState(false);

  // Group materials by type and calculate totals
  const materialStats = transactions.reduce((acc, transaction) => {
    transaction?.all_materials?.forEach((material) => {
      const key = material.material_name;
      if (!acc[key]) {
        acc[key] = { name: key, amount: 0, value: 0 };
      }
      acc[key].amount += material.total_price;
      acc[key].value += material.quantity;
    });
    return acc;
  }, {});

  // Build and sort chart data
  const totalAmount = transactions.reduce((sum, t) => sum + t.total_price, 0);
  const chartData = Object.values(materialStats)
    .sort((a, b) => b.amount - a.amount)
    .map((stat) => ({
      ...stat,
      percent: Math.round((stat.amount / totalAmount) * 100),
    }));

  // Decide which slice of data to show
  const displayedData = showAll ? chartData : chartData.slice(0, 4);

  const COLORS = ["#46237A", "#6B3FA0", "#8F6AC2", "#B394E8", "#D7C6F5"];

  return (
    <div className="flex flex-col md:flex-row items-center justify-between bg-white p-6 rounded-lg mb-6">
      <div className="w-64 h-64 relative">
        <PieChart width={280} height={280}>
          <Pie
            data={displayedData}
            cx={125}
            cy={125}
            startAngle={90}
            endAngle={-270}
            innerRadius={85}
            outerRadius={110}
            paddingAngle={2}
            dataKey="percent"
          >
            {displayedData.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-gray-500">Total Material</div>
          <div className="text-xl font-semibold">
            ₹{totalAmount.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="flex-1 ml-0 md:ml-8 mt-6 md:mt-0">
        <h3 className="text-lg font-medium mb-4">Material Distribution</h3>
        <div className="space-y-3 max-h-64 overflow-auto">
          {displayedData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span>{item.name}</span>
              </div>
              <div className="flex flex-row items-center  w-[20%] justify-between">
                <span className="font-medium">{item.percent}%</span>
                <span className="text-gray-500">
                  ₹{item.amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>

        {chartData.length > 4 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-4 text-indigo-600 hover:underline"
          >
            {showAll ? "Show Less" : "Show More"}
          </button>
        )}
      </div>
    </div>
  );
};

const formatTransactionDateTime = (tran) => {
  const datePart = new Date(
    tran.date || tran.transaction_date,
  ).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
  const timePart = new Date(tran.created_on).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
};

const PendingStats = ({ transactions }) => {
  // Group by vendor for Material transactions and by worker for Worker transactions
  const stats = transactions.reduce((acc, t) => {
    let groupKey;

    // Determine grouping based on transaction type
    if (t.transaction_type === "Worker") {
      // Group workers by name and work type
      groupKey = `${t.worker_name} - ${t.worker_workname}`;
    } else {
      // Group materials by vendor name
      groupKey = t.vendor_name || "Unknown Vendor";
    }

    const amount = t.pending_amount || 0;

    if (!acc[groupKey]) {
      acc[groupKey] = {
        name: groupKey,
        amount: 0,
        count: 0,
        type: t.transaction_type,
      };
    }
    acc[groupKey].amount += amount;
    acc[groupKey].count += 1;
    return acc;
  }, {});

  // Calculate total pending amount
  let totalPending = 0;
  transactions.forEach((t) => {
    totalPending += t.pending_amount || 0;
  });

  const chartData = Object.values(stats).map((v) => ({
    ...v,
    value: v.amount, // use raw amount for consistent slice proportions
  }));

  if (!chartData.length) {
    return (
      <div className="bg-white p-6 rounded-lg mb-6 text-gray-500">
        No pending transactions available.
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between bg-white p-6 rounded-lg mb-6">
      {/* Pie Chart */}
      <div className="w-64 h-64 relative">
        <PieChart width={280} height={280}>
          <Pie
            data={chartData}
            cx={125}
            cy={125}
            startAngle={90}
            endAngle={-270}
            innerRadius={85}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs text-gray-500">Total Pending</div>
          <div className="text-xl font-semibold text-red-500">
            ₹{totalPending.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Stats Distribution */}
      <div className="flex-1 ml-8">
        <h3 className="text-lg font-medium mb-4">Pending Distribution</h3>
        <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
          {chartData.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full mr-3"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <div className="flex flex-col">
                  <span className="text-sm">{item.name}</span>
                  <span className="text-xs text-gray-400">{item.type}</span>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {item.count} transaction{item.count !== 1 ? "s" : ""}
                </span>
                <span className="font-medium w-16 text-right">
                  {Math.round((item.amount / totalPending) * 100)}%
                </span>
                <span className="text-red-500 w-24 text-right">
                  ₹{item.amount.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Finance = ({
  userPermissions,
  setTab,
  setDelivery,
  permissionsLoading,
}) => {
  const canEditFinance = Boolean(
    userPermissions?.edit_finance ||
      userPermissions?.is_admin ||
      userPermissions?.edit_all,
  );
  const [ITEMS_PER_PAGE, SET_ITEMS_PER_PAGE] = useState(10);
  const [transactions, setTransactions] = useState(
    localStorage.getItem("site")
      ? localStorage.getItem(`${localStorage.getItem("site")}-transactions`)
        ? JSON.parse(
          localStorage.getItem(
            `${localStorage.getItem("site")}-transactions`,
          ),
        )
        : []
      : [],
  );
  const [loading, setLoading] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState("finance");
  const [refresing, setRefresing] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pieData, setPieData] = useState(
    localStorage.getItem("site")
      ? localStorage.getItem(`${localStorage.getItem("site")}-pieData`)
        ? JSON.parse(
          localStorage.getItem(`${localStorage.getItem("site")}-pieData`),
        )
        : []
      : [],
  );
  const [totalAmount, setTotalAmount] = useState(0);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [transactionCatagories, setTransactionCatagories] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState(
    localStorage.getItem("site")
      ? localStorage.getItem(
        `${localStorage.getItem("site")}-pendingTransactions`,
      )
        ? JSON.parse(
          localStorage.getItem(
            `${localStorage.getItem("site")}-pendingTransactions`,
          ),
        )
        : []
      : [],
  );
  // const [refundCategoryId, setRefundCategoryId] = useState(null);
  // const [otherCategoryId, setOtherCategoryId] = useState(null);
  // const [materialCategoryId, setMaterialCategoryId] = useState(null);
  // const [workerCategoryId, setWorkerCategoryId] = useState(null);
  // const [receivedCategoryId, setReceiveCategoryId] = useState(null);
  const [showMaterialPurchaseDetails, setShowMaterialPurchaseDetails] =
    useState(false);
  const alert = useAlert();
  // const [CATEGORY_NAMES, setCategoryNames] = useState({});
  const CATEGORY_NAMES =
    JSON.parse(localStorage.getItem("categoryNames")) || {};
  const materialCategoryId =
    parseInt(localStorage.getItem("materialCategory")) || null;
  const otherCategoryId =
    parseInt(localStorage.getItem("otherCategory")) || null;
  const workerCategoryId =
    parseInt(localStorage.getItem("workerCategory")) || null;
  const refundCategoryId =
    parseInt(localStorage.getItem("refundCategory")) || null;
  const receivedCategoryId =
    parseInt(localStorage.getItem("receivedCategory")) || null;
  console.log(
    "categories",
    materialCategoryId,
    otherCategoryId,
    workerCategoryId,
    refundCategoryId,
    receivedCategoryId,
  );
  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          setTransactionCatagories(data);
          const names = {};
          data.forEach((cat) => {
            names[cat.id] = cat.catagory_name;
          });
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  const OriginalTransactionType = (
    transactionId,
    originalTransactionCatagoryId,
  ) => {
    if (originalTransactionCatagoryId !== null) {
      const category = transactionCatagories.find(
        (cat) => cat.id === originalTransactionCatagoryId,
      );

      if (category?.catagory_name === "General Transaction") {
        const transaction = transactions.find((t) => t.id === transactionId);
        return transaction?.transaction_type;
      }
      return category?.catagory_name;
    }
    const matchingTransactions = transactions.filter(
      (t) => t.id === transactionId,
    );
    if (matchingTransactions.length >= 1) {
      // If there are multiple matching transactions, return the type of the first one
      return matchingTransactions[0]?.transaction_type || "N/A";
    }
    return "N/A";
  };

  const OriginalTransactionAmount = (transactionId) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    return transaction?.paid_amount || transaction?.amount || 0;
  };

  const handleCloseModal = () => {
    setSelectedTransaction(null);
  };
  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (transactions.length > 0) {
      calculatePieData();
      filterTransactions();
    }
  }, [transactions, activeTab]);

  // Reset current page when filtered transactions change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredTransactions.length]);

  const filterTransactions = () => {
    let filtered = [...transactions];

    switch (activeTab) {
      case "material":
        filtered = filtered.filter(
          (t) => t.transaction_catagory_id === materialCategoryId,
        );
        break;
      case "workers":
        filtered = filtered.filter(
          (t) => t.transaction_catagory_id === workerCategoryId,
        );
        break;
      case "refund":
        filtered = filtered.filter(
          (t) => t.transaction_catagory_id === refundCategoryId,
        );
        break;
      case "pending":
        filtered = pendingTransactions;
        break;
      case "others":
        filtered = filtered.filter(
          (t) =>
            t.transaction_catagory_id === otherCategoryId &&
            t.original_transaction_id === null,
        );
        // filter 0 amount transactions
        filtered = filtered.filter((t) => t?.paid_amount !== 0);
        break;
      case "received":
        filtered = filtered.filter(
          (t) => t.transaction_catagory_id === receivedCategoryId,
        );
        break;
      default:
        // filter out transactions with 0 amount
        filtered = filtered.filter((t) => t?.paid_amount !== 0);
        break;
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1);
  };

  // Get total number of pages
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);

  // Ensure current page is within bounds
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  // Get current transactions for the page
  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const calculatePieData = () => {
    let categoryTotals = {};
    // Initialize totals for each category
    transactionCatagories.forEach((cat) => {
      categoryTotals[cat.id] = 0;
    });

    transactions.forEach((transaction) => {
      const categoryId = transaction?.transaction_catagory_id;
      const amount = transaction?.amount ?? transaction?.paid_amount ?? 0;

      if (categoryId in categoryTotals) {
        categoryTotals[categoryId] += amount;
      }
    });

    console.log("CATEGORY TOTALS", categoryTotals);

    const total = Object.values(categoryTotals).reduce(
      (sum, amount) => sum + amount,
      0,
    );
    // total amount without refund i.e category 7
    const totalWithoutRefund = Object.entries(categoryTotals).reduce(
      (sum, [key, amount]) => {
        const catId = parseInt(key, 10);
        if (catId === receivedCategoryId) {
          return sum;
        }
        if (catId === refundCategoryId) {
          return sum - amount;
        }
        return sum + amount;
      },
      0,
    );

    setTotalAmount(totalWithoutRefund);
    const pieData = Object.entries(categoryTotals)
      .filter(([_, amount]) => amount > 0)
      .map(([categoryId, amount]) => ({
        name: CATEGORY_NAMES[categoryId],
        value: Math.round((amount / total) * 100),
        amount: amount,
      }))
      .sort((a, b) => b.value - a.value);

    setPieData(pieData);
    localStorage.setItem(
      `${localStorage.getItem("site")}-pieData`,
      JSON.stringify(pieData),
    );
  };

  const fetchTransactions = async () => {
    if (transactions.length === 0 && pendingTransactions.length === 0) {
      setLoading(true);
    }
    try {
      const site = localStorage.getItem("site");
      // const token = localStorage.getItem("access");

      // const response = await fetch(
      //   `${BASE_URL}/finance/get-all-transactions-of-a-site/`,
      //   {
      //     method: "POST",
      //     headers: {
      //       Authorization: `Bearer ${token}`,
      //       "Content-Type": "application/json",
      //     },
      //     body: JSON.stringify({ sites: site }),
      //   }
      // );

      // const data = await response.json();
      const data = await FinanceApi.listAllFinancebySite({
        sites: site,
      });

      // Fetch pending transactions
      const pendingData = await FinanceApi.listPendingTransactionsBySite({
        sites: site,
      });

      if (data.status) {
        let allTransactions = [...data.data];

        // Transform and merge pending transactions
        if (pendingData.status) {
          // Transform material pending transactions
          let unifiedPendingTransactions = [];

          if (pendingData.pending_material_transactions) {
            const transformedPendingMaterials =
              pendingData.pending_material_transactions.map((pt) => ({
                ...pt,
                transaction_type: "Material",
                transaction_catagory_id:
                  pt.transaction_catagory_id || materialCategoryId,
                description: pt?.po_number
                  ? `${pt.vendor_name} - ${pt.po_number}`
                  : `DLVRY-${convertToStringIndex(pt.id)}`,
                vendor_name: pt.vendor_name || "",
                all_materials: pt.all_materials || [],
                pending_amount: pt.remaining_payment,
                total_paid: pt.paid_amount,
                total_amount: pt.total_price,
              }));
            unifiedPendingTransactions = [...transformedPendingMaterials];
          }

          // Transform worker pending transactions
          if (pendingData.pending_worker_transactions) {
            const transformedPendingWorkers =
              pendingData.pending_worker_transactions.map((wt, index) => ({
                id: `worker-${wt.worker_id}`,
                worker_id: wt.worker_id,
                worker_name: wt.worker_name,
                worker_workname: wt.worker_workname,
                transaction_type: "Worker",
                // transaction_catagory_id: workerCategoryId,
                description: `${wt.worker_name} - ${wt.worker_workname}`,
                pending_amount: wt.pending_amount,
                total_paid: wt.total_paid,
                total_amount: wt.total_amount,
                days_present: wt.days_present,
                half_days: wt.half_days,
                overtime_days: wt.overtime_days,
                // Add fields to match transaction structure
                date: new Date().toISOString().split("T")[0],
                created_on: new Date().toISOString(),
              }));
            unifiedPendingTransactions = [
              ...unifiedPendingTransactions,
              ...transformedPendingWorkers,
            ];
          }

          // Replace or merge pending transactions
          setPendingTransactions(unifiedPendingTransactions);
          localStorage.setItem(
            `${site}-pendingTransactions`,
            JSON.stringify(unifiedPendingTransactions),
          );
        }

        setTransactions(allTransactions);
        localStorage.setItem(
          `${site}-transactions`,
          JSON.stringify(allTransactions),
        );
        setFilteredTransactions(allTransactions);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
      setRefresing(false);
    }
  };

  const formatAmount = (amount) => {
    const value = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getFormattedTransactionId = (transaction) => {
    switch (transaction.transaction_catagory_id) {
      case materialCategoryId:
        return transaction?.po_number
          ? `${transaction.po_number}`
          : `DLVRY-${convertToStringIndex(transaction.id)}`;
      case workerCategoryId:
        return `WORKER_PAY-${convertToStringIndex(transaction.id)}`;
      default:
        return `TRN-${convertToStringIndex(transaction.id)}`;
    }
  };

  const handleRefresh = () => {
    setRefresing(true);
    fetchTransactions();
  };

  const handlePageChange = (pageNumber) => {
    // Ensure the page number stays within bounds
    const newPage = Math.min(Math.max(1, pageNumber), totalPages);
    setCurrentPage(newPage);
  };

  // Generate pagination numbers with improved visibility of current range
  // Generate pagination numbers to reflect the correct page range
  const generatePaginationNumbers = () => {
    const pages = [];
    const range = 2; // Number of pages to show around `currentPage`

    const start = Math.max(1, currentPage - range);
    const end = Math.min(totalPages, currentPage + range);

    if (start > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 rounded-lg hover:bg-gray-100"
        >
          1
        </button>,
      );
      if (start > 2) pages.push(<span key="start-ellipsis">...</span>);
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg ${currentPage === i ? "bg-[#3B0069] text-white" : "hover:bg-gray-100"
            }`}
        >
          {i}
        </button>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<span key="end-ellipsis">...</span>);
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 rounded-lg hover:bg-gray-100"
        >
          {totalPages}
        </button>,
      );
    }

    return pages;
  };
  // Rest of the component remains the same...

  const handleOpenModal = (transaction) => {
    // switch case
    switch (transaction) {
      case "material":
        setShowMaterialModal(true);
        break;
      case "worker":
        setShowWorkerModal(true);
        break;
      case "refund":
        setShowRefundModal(true);
        break;
      case "other":
        setShowOtherModal(true);
        break;
      case "receive":
        setShowReceiveModal(true);
        break;
      default:
        break;
    }
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-10 h-10 animate-spin text-purple-500" />
      </div>
    );
  }
  if (showMaterialPurchaseDetails) {
    return (
      <MaterialPurchaseDetails
        onClose={() => {
          setShowMaterialPurchaseDetails(false);
          fetchTransactions();
          // Remove handleCloseModal() since TransactionModal is already closed
        }}
        Delivery={selectedTransaction}
        setDelivery={setDelivery}
      // setTab={setTab}
      />
    );
  }

  if (showMaterialModal) {
    return (
      <MaterialPurchaseForm
        onClose={() => {
          setShowMaterialModal(false);
          fetchTransactions();
          // TransactionModal is already closed, no need for additional cleanup
        }}
      />
    );
  }

  if (showWorkerModal) {
    return (
      <WorkersPaymentForm
        onClose={() => {
          setShowWorkerModal(false);
          fetchTransactions();
          // TransactionModal is already closed
        }}
        setTab={setTab}
      />
    );
  }

  if (showRefundModal) {
    return (
      <RefundForm
        onClose={() => {
          setShowRefundModal(false);
          fetchTransactions();
          // TransactionModal is already closed
        }}
      />
    );
  }

  if (showOtherModal) {
    return (
      <OtherPaymentForm
        onClose={() => {
          setShowOtherModal(false);
          fetchTransactions();
          // TransactionModal is already closed
        }}
      />
    );
  }

  if (showReceiveModal) {
    return (
      <RecievedPaymentForm
        onClose={() => {
          setShowReceiveModal(false);
          fetchTransactions();
          // TransactionModal is already closed
        }}
      />
    );
  }

  if (
    !userPermissions?.view_finance &&
    !userPermissions?.is_admin &&
    !userPermissions?.view_all &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module={"Finance"}
        description={
          "You don't have permission to view financial information for this site. Please contact your administrator for access. "
        }
      />
    );
  }

  return (
    <div
      className="p-6 bg-gray-50 h-full w-full"
      style={{
        overflowY: "auto",
        scrollbarWidth: "none",
      }}
    >
      <div className="flex space-x-4 mb-6 pt-2">
        <button
          onClick={() => setActiveMainTab("finance")}
          className={`pb-2 px-4 font-semibold transition-all border-b-2 ${activeMainTab === "finance"
              ? "text-sm-primary border-sm-primary"
              : "text-gray-400 border-transparent hover:text-gray-600 hover:border-gray-200"
            }`}
        >
          Project Finance
        </button>
        {/* <button
          onClick={() => setActiveMainTab("pettycash")}
          className={`pb-2 px-4 font-semibold transition-all border-b-2 ${
            activeMainTab === "pettycash"
              ? "text-sm-primary border-sm-primary"
              : "text-gray-400 border-transparent hover:text-gray-600 hover:border-gray-200"
          }`}
        >
          Petty Cash Management
        </button> */}
      </div>

      <LoadingScreen loading={loading}>
        {activeMainTab === "finance" ? (
          <>
            {/* Stats Section */}
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Transactions Stats</h2>
                <button
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  onClick={handleRefresh}
                >
                  <RefreshCw
                    size={20}
                    className={refresing ? "animate-spin" : ""}
                  />
                </button>
              </div>
              {activeTab === "material" ? (
                <MaterialStats transactions={filteredTransactions} />
              ) : activeTab === "all" ? (
                <div className="flex items-center justify-between bg-white p-6 rounded-lg mb-6">
                  <div className="w-64 h-64 relative">
                    <PieChart width={280} height={280}>
                      <Pie
                        data={pieData}
                        cx={125}
                        cy={125}
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-xs text-gray-500">Total Expense</div>
                      <div className="text-xl font-semibold">
                        {formatAmount(totalAmount)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 w-[60%] gap-4">
                    {pieData.map((item, index) => (
                      <div
                        key={item.name}
                        className="flex items-center border-b-[0.5px] border-gray-400 pb-1"
                      >
                        <div
                          className="w-4 h-4 rounded-full mr-3"
                          style={{ backgroundColor: COLORS[index] }}
                        />
                        <span className="flex-1">{item.name}</span>
                        <span className="font-semibold text-right w-24">
                          {item.value}%
                        </span>
                        <span className="text-gray-500 text-sm ml-4 w-32 text-right">
                          {formatAmount(item.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : activeTab === "workers" ? (
                <WorkerStats transactions={filteredTransactions} />
              ) : activeTab === "refund" ? (
                <RefundStats transactions={filteredTransactions} />
              ) : activeTab === "pending" ? (
                <PendingStats transactions={filteredTransactions} />
              ) : activeTab === "others" ? (
                <OtherStats transactions={filteredTransactions} />
              ) : activeTab === "received" ? (
                <ReceivedStats transactions={filteredTransactions} />
              ) : null}
            </div>

            {/* Transactions Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Today's Transaction</h2>
                <button
                  className="bg-[#3B0069] text-white px-4 py-2 rounded-lg flex items-center"
                  onClick={() => {
                    if (!canEditFinance) {
                      alert.addAlert(
                        "You don't have permission to add transaction",
                        "error",
                      );
                      return;
                    }
                    setIsModalOpen(true);
                  }}
                >
                  <Plus size={20} className="mr-2" />
                  Transaction
                </button>
              </div>

              {/* Tabs */}
              <div className="flex space-x-6 mb-3 border-b">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`flex items-center pb-2 ${activeTab === "all"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <ClipboardList size={20} className="mr-2" />
                  All Transactions
                </button>
                <button
                  onClick={() => setActiveTab("material")}
                  className={`flex items-center pb-2 ${activeTab === "material"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <Package size={20} className="mr-2" />
                  Material
                </button>
                <button
                  onClick={() => setActiveTab("workers")}
                  className={`flex items-center pb-2 ${activeTab === "workers"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <Users size={20} className="mr-2" />
                  Workers
                </button>
                <button
                  onClick={() => setActiveTab("refund")}
                  className={`flex items-center pb-2 ${activeTab === "refund"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <RefreshCw size={20} className="mr-2" />
                  Refund
                </button>
                <button
                  onClick={() => setActiveTab("pending")}
                  className={`flex items-center pb-2 ${activeTab === "pending"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <Clock size={20} className="mr-2" />
                  Pending transaction
                </button>
                <button
                  onClick={() => setActiveTab("others")}
                  className={`flex items-center pb-2 ${activeTab === "others"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <MdOutlinePayments size={20} className="mr-2" />
                  Others
                </button>
                <button
                  onClick={() => setActiveTab("received")}
                  className={`flex items-center pb-2 ${activeTab === "received"
                      ? "border-b-2 border-purple-700 text-purple-700"
                      : "text-gray-500"
                    }`}
                >
                  <AiOutlineVerticalAlignBottom size={20} className="mr-2" />
                  Received
                </button>
              </div>

              {/* Table */}
              {activeTab === "material" ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">
                          Transaction ID
                        </th>
                        <th className="py-3 px-4 font-medium">Content</th>
                        <th className="py-3 px-4 font-medium">Vendor</th>
                        <th className="py-3 px-4 font-medium">Type</th>
                        <th className="py-3 px-4 font-medium">Medium</th>
                        <th className="py-3 px-4 font-medium">Date</th>
                        <th className="py-3 px-4 font-medium">Amount</th>
                        <th className="py-3 px-4 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b text-sm hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              {transaction?.po_number
                                ? `${transaction?.po_number}`
                                : `Delivery${transaction?.id}`}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-col items-start">
                                <span className="font-normal">
                                  {transaction?.all_materials?.length
                                    ? transaction?.all_materials[0]
                                      ?.material_name +
                                    " " +
                                    transaction?.all_materials[0]
                                      ?.material_subcategory
                                    : "No materials"}
                                </span>
                                <span className="text-sm">
                                  {transaction?.all_materials?.length > 1
                                    ? "+" +
                                    transaction?.all_materials?.length +
                                    " others"
                                    : ""}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.vendor_name ||
                                transaction?.vendor ||
                                "N/A"}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.payment_status}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.payment_method ||
                                transaction?.transaction_method}
                            </td>
                            <td className="py-3 px-4">
                              {formatTransactionDateTime(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={
                                  transaction?.transaction_catagory_id ===
                                    refundCategoryId ||
                                    transaction?.transaction_catagory_id ===
                                    receivedCategoryId
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {transaction?.transaction_catagory_id ===
                                  refundCategoryId ||
                                  transaction?.transaction_catagory_id ===
                                  receivedCategoryId
                                  ? "↑"
                                  : "↓"}
                                {formatAmount(
                                  transaction?.paid_amount ??
                                  transaction?.amount,
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === "all" ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">Description</th>
                        <th className="py-3 px-4 font-medium">
                          Transaction ID
                        </th>
                        <th className="py-3 px-4 font-medium">Type</th>
                        <th className="py-3 px-4 font-medium">Medium</th>
                        <th className="py-3 px-4 font-medium">Date</th>
                        <th className="py-3 px-4 font-medium">Amount</th>
                        <th className="py-3 px-4 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              {transaction?.transaction_type ||
                                (transaction?.transaction_catagory_id ===
                                  materialCategoryId
                                  ? "Material Purchase"
                                  : transaction?.transaction_catagory_id ===
                                    workerCategoryId
                                    ? "Worker Transaction"
                                    : transaction?.transaction_catagory_id ===
                                      refundCategoryId
                                      ? "Refund"
                                      : transaction?.transaction_catagory_id ===
                                        receivedCategoryId
                                        ? "Received"
                                        : "Others")}
                            </td>
                            <td className="py-3 px-4">
                              {getFormattedTransactionId(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.transaction_catagory_id ===
                                receivedCategoryId
                                ? "Received"
                                : transaction?.payment_status ||
                                transaction?.transaction_status ||
                                "pre-paid"}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.payment_method ||
                                transaction?.transaction_method}
                            </td>
                            <td className="py-3 px-4">
                              {formatTransactionDateTime(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={
                                  transaction?.transaction_catagory_id ===
                                    refundCategoryId ||
                                    transaction?.transaction_catagory_id ===
                                    receivedCategoryId
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {transaction?.transaction_catagory_id ===
                                  refundCategoryId ||
                                  transaction?.transaction_catagory_id ===
                                  receivedCategoryId
                                  ? "↑"
                                  : "↓"}
                                {formatAmount(
                                  transaction?.paid_amount ??
                                  transaction?.amount,
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === "workers" ? (
                // <>
                //   <div className="space-y-2">
                //     {currentTransactions.map((transaction) => (
                //       <WorkerRow
                //         key={transaction?.id}
                //         transaction={transaction}
                //         handleClick={() => {
                //           setSelectedTransaction(transaction);
                //         }}
                //       />
                //     ))}
                //   </div>
                // </>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">Worker</th>
                        <th className="py-3 px-4 font-medium">Worker type</th>
                        <th className="py-3 px-4 font-medium">
                          Transaction ID
                        </th>
                        <th className="py-3 px-4 font-medium">Paid by</th>
                        <th className="py-3 px-4 font-medium">Medium</th>
                        <th className="py-3 px-4 font-medium">Date</th>
                        <th className="py-3 px-4 font-medium">Amount</th>
                        <th className="py-3 px-4 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b text-sm hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              {transaction?.worker_name}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.worker_workname}
                            </td>
                            <td className="py-3 px-4">
                              WORKER_PAY-{convertToStringIndex(transaction?.id)}
                            </td>
                            <td className="py-3 px-4">
                              {transaction.created_by}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.payment_method ||
                                transaction?.transaction_method}
                            </td>
                            <td className="py-3 px-4">
                              {formatTransactionDateTime(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={
                                  transaction?.transaction_catagory_id ===
                                    refundCategoryId ||
                                    transaction?.transaction_catagory_id ===
                                    receivedCategoryId
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {transaction?.transaction_catagory_id ===
                                  refundCategoryId ||
                                  transaction?.transaction_catagory_id ===
                                  receivedCategoryId
                                  ? "↑"
                                  : "↓"}
                                {formatAmount(
                                  transaction?.paid_amount ??
                                  transaction?.amount,
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === "refund" ? (
                // <>
                //   <div className="space-y-2">
                //     {currentTransactions.map((transaction) => (
                //       <RefundRow
                //         key={transaction?.id}
                //         transaction={transaction}
                //         handleClick={() => {
                //           setSelectedTransaction(transaction);
                //         }}
                //       />
                //     ))}
                //   </div>
                // </>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">
                          Transaction ID
                        </th>
                        <th className="py-3 px-4 font-medium">Created by</th>
                        <th className="py-3 px-4 font-medium">Type</th>
                        <th className="py-3 px-4 font-medium">Date</th>
                        <th className="py-3 px-4 font-medium">Paid Amount</th>
                        <th className="py-3 px-4 font-medium">Refund Amount</th>
                        <th className="py-3 px-4 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b text-sm hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {/* find original transaction and its type from transactions matching id with this original_transaction_id */}
                                  {OriginalTransactionType(
                                    transaction?.original_transaction_id,
                                    transaction?.original_transaction_catagory_id,
                                  )}
                                </span>
                                <span className="text-gray-500">
                                  TRN-{convertToStringIndex(transaction?.id)}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.created_by}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.transaction_status}
                            </td>
                            {/* <td className="py-3 px-4">
                        {transaction?.payment_method ||
                          transaction?.transaction_method}
                      </td> */}
                            <td className="py-3 px-4">
                              {formatTransactionDateTime(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-black font-medium">
                                {formatAmount(
                                  OriginalTransactionAmount(
                                    transaction?.original_transaction_id,
                                  ),
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={
                                  transaction?.transaction_catagory_id ===
                                    refundCategoryId ||
                                    transaction?.transaction_catagory_id ===
                                    receivedCategoryId
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {formatAmount(
                                  transaction?.paid_amount ??
                                  transaction?.amount,
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === "pending" ? (
                // <>
                //   <div className="space-y-2">
                //     {currentTransactions.map((transaction) => (
                //       <PendingRow
                //         transaction={transaction}
                //         key={transaction?.id}
                //         handleClick={() => {
                //           // setDelivery(transaction);
                //           setSelectedTransaction(transaction);
                //           setShowMaterialPurchaseDetails(true);
                //         }}
                //       />
                //     ))}
                //   </div>
                // </>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">
                          Transaction Type
                        </th>
                        <th className="py-3 px-4 font-medium">Description</th>
                        <th className="py-3 px-4 font-medium">
                          Pending Amount
                        </th>
                        <th className="py-3 px-4 font-medium">Paid Amount</th>
                        <th className="py-3 px-4 font-medium">Total Amount</th>
                        <th className="py-3 px-4 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No pending transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b text-sm hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${transaction?.transaction_type === "Material"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-blue-100 text-blue-800"
                                  }`}
                              >
                                {transaction?.transaction_type || "Material"}
                              </span>
                            </td>

                            <td className="py-3 px-4">
                              {transaction?.description ||
                                transaction?.po_number ||
                                `DLVRY-${convertToStringIndex(transaction?.id)}`}
                            </td>

                            <td className="py-3 px-4">
                              <span className="font-medium text-red-600">
                                {formatAmount(
                                  transaction?.pending_amount ||
                                  transaction?.remaining_payment,
                                )}
                              </span>
                            </td>

                            <td className="py-3 px-4">
                              <span className="text-black font-medium">
                                {formatAmount(
                                  transaction?.total_paid ||
                                  transaction?.paid_amount ||
                                  transaction?.amount,
                                )}
                              </span>
                            </td>

                            <td className="py-3 px-4">
                              <span className="text-black font-medium">
                                {formatAmount(
                                  transaction?.total_amount ||
                                  transaction?.total_price,
                                )}
                              </span>
                            </td>

                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === "others" ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">
                          Transaction ID
                        </th>
                        <th className="py-3 px-4 font-medium">Purpose</th>
                        <th className="py-3 px-4 font-medium">Medium</th>
                        <th className="py-3 px-4 font-medium">Date</th>
                        <th className="py-3 px-4 font-medium">Amount</th>
                        <th className="py-3 px-4 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b text-sm hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              TRN-{convertToStringIndex(transaction?.id)}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.transaction_type}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.payment_method ||
                                transaction?.transaction_method}
                            </td>
                            <td className="py-3 px-4">
                              {formatTransactionDateTime(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={
                                  transaction?.transaction_catagory_id ===
                                    refundCategoryId ||
                                    transaction?.transaction_catagory_id ===
                                    receivedCategoryId
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {transaction?.transaction_catagory_id ===
                                  refundCategoryId ||
                                  transaction?.transaction_catagory_id ===
                                  receivedCategoryId
                                  ? "↑"
                                  : "↓"}
                                {formatAmount(
                                  transaction?.paid_amount ??
                                  transaction?.amount,
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === "received" ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-[#718EBF] ">
                        <th className="py-3 px-4 font-medium">
                          Transaction ID
                        </th>
                        <th className="py-3 px-4 font-medium">Purpose</th>
                        <th className="py-3 px-4 font-medium">Initiated by</th>
                        <th className="py-3 px-4 font-medium">Medium</th>
                        <th className="py-3 px-4 font-medium">Date</th>
                        <th className="py-3 px-4 font-medium">Amount</th>
                        <th className="py-3 px-4 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : currentTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="flex flex-col items-center justify-center py-6">
                              <img
                                src={FinanceIcon}
                                alt="No transactions found"
                                className="mb-4"
                                width={240}
                              />
                              <span className="text-gray-500 text-xl">
                                No transactions found
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentTransactions.map((transaction) => (
                          <tr
                            key={`${transaction?.id}-${transaction?.transaction_catagory_id}`}
                            className="border-b text-sm hover:bg-gray-100 cursor-pointer"
                          >
                            <td className="py-3 px-4">
                              TRN-{convertToStringIndex(transaction?.id)}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.transaction_type}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.created_by}
                            </td>
                            <td className="py-3 px-4">
                              {transaction?.payment_method ||
                                transaction?.transaction_method}
                            </td>
                            <td className="py-3 px-4">
                              {formatTransactionDateTime(transaction)}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={
                                  transaction?.transaction_catagory_id ===
                                    refundCategoryId ||
                                    transaction?.transaction_catagory_id ===
                                    receivedCategoryId
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {transaction?.transaction_catagory_id ===
                                  refundCategoryId ||
                                  transaction?.transaction_catagory_id ===
                                  receivedCategoryId
                                  ? "↑"
                                  : "↓"}
                                {formatAmount(
                                  transaction?.paid_amount ??
                                  transaction?.amount,
                                )}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className="text-purple-700 font-medium hover:text-purple-900 px-3 py-1 rounded-3xl border-[1px] border-[#3B0069]"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {/* Pagination */}
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-gray-500">
                  Showing{" "}
                  {Math.min(
                    filteredTransactions.length,
                    (validCurrentPage - 1) * ITEMS_PER_PAGE + 1,
                  )}{" "}
                  to{" "}
                  {Math.min(
                    validCurrentPage * ITEMS_PER_PAGE,
                    filteredTransactions.length,
                  )}{" "}
                  of {filteredTransactions.length} entries
                </div>
                {/* CUSTOM ITEMS PER PAGE INPUT */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-500">
                    Items per page:
                  </label>
                  <select
                    value={ITEMS_PER_PAGE}
                    onChange={(e) => SET_ITEMS_PER_PAGE(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg p-1"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(validCurrentPage - 1)}
                    disabled={validCurrentPage === 1}
                    className={`p-2 rounded-lg ${validCurrentPage === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "hover:bg-gray-100"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  {generatePaginationNumbers()}
                  <button
                    onClick={() => handlePageChange(validCurrentPage + 1)}
                    disabled={validCurrentPage === totalPages}
                    className={`p-2 rounded-lg ${validCurrentPage === totalPages
                        ? "text-gray-300 cursor-not-allowed"
                        : "hover:bg-gray-100"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
              {selectedTransaction &&
                (activeTab === "material" ||
                  selectedTransaction.transaction_catagory_id ===
                  materialCategoryId ? (
                  <MaterialPurchaseModal
                    transaction={selectedTransaction}
                    setDelivery={setDelivery}
                    setTab={setTab}
                    setShowMaterialPurchaseDetails={
                      setShowMaterialPurchaseDetails
                    }
                    isPendingView={activeTab === "pending"}
                    canEditFinance={canEditFinance}
                    onClose={handleCloseModal} // Pass the close handler here
                  />
                ) : activeTab === "pending" &&
                  selectedTransaction?.transaction_type === "Worker" ? (
                  <WorkerPendingModal
                    transaction={selectedTransaction}
                    onClose={handleCloseModal}
                  />
                ) : activeTab === "workers" ||
                  selectedTransaction?.transaction_catagory_id ===
                  workerCategoryId ? (
                  <WorkerTransactionModal
                    transaction={selectedTransaction}
                    onClose={handleCloseModal} // Pass the close handler here
                  />
                ) : activeTab === "refund" ||
                  selectedTransaction?.transaction_catagory_id ===
                  refundCategoryId ? (
                  <RefundTransactionModal
                    transaction={selectedTransaction}
                    onClose={handleCloseModal} // Pass the close handler here
                  />
                ) : (activeTab === "all" ||
                  activeTab === "others" ||
                  activeTab === "received") &&
                  (selectedTransaction?.transaction_catagory_id ===
                    otherCategoryId ||
                    selectedTransaction?.transaction_catagory_id ===
                    receivedCategoryId) ? (
                  <OtherTransactionModal
                    transaction={selectedTransaction}
                    onUpdate={fetchTransactions}
                    isReceivedTransaction={
                      selectedTransaction?.transaction_catagory_id ===
                      receivedCategoryId
                    }
                    canEditFinance={canEditFinance}
                    onClose={handleCloseModal} // Pass the close handler here
                  />
                ) : null)}
            </div>

            <TransactionModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              handleClick={handleOpenModal}
              setTab={setTab}
            />
          </>
        ) : (
          <PettyCashDashboard />
        )}
      </LoadingScreen>
    </div>
  );
};

export default Finance;
